import { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import { Sparkles, Send, X, Plus, Loader2, MessageSquare, Trash2, ChevronLeft } from "lucide-react";
import ReactMarkdown from "react-markdown";

const CoPilotContext = createContext({ open: () => {}, close: () => {}, toggle: () => {}, openWithSeed: () => {}, refreshNudges: () => {} });
export const useCoPilot = () => useContext(CoPilotContext);

const MessageBubble = ({ msg }) => {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-4`}>
      <div
        className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed ${
          isUser
            ? "bg-crimson text-white rounded-2xl rounded-br-sm"
            : "bg-[#1a1a1a] text-gray-100 border border-[#2a2a2a] rounded-2xl rounded-bl-sm"
        }`}
        data-testid={`copilot-msg-${msg.role}`}
      >
        {isUser ? (
          <span className="whitespace-pre-wrap">{msg.content}</span>
        ) : (
          <div className="prose prose-invert prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-li:my-0.5 prose-strong:text-white prose-code:text-crimson prose-code:bg-black/40 prose-code:px-1 prose-code:py-0.5 prose-code:rounded">
            <ReactMarkdown>{msg.content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
};

export const CoPilotProvider = ({ children }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat"); // "chat" | "history"
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);
  const openFn = useCallback(() => setOpen(true), []);
  const toggle = useCallback(() => setOpen(p => !p), []);
  // Open Co-Pilot pre-seeded with a prompt — useful for nudge actions.
  // Either auto-sends or just fills the input depending on `autoSend` flag.
  const openWithSeedRef = useRef(null);
  const openWithSeed = useCallback((seedPrompt, autoSend = true) => {
    if (!seedPrompt) { setOpen(true); return; }
    // Reset to a fresh chat so context is clean
    setSessionId(null);
    setMessages([]);
    setInput("");
    setOpen(true);
    setView("chat");
    if (autoSend) {
      // Defer to next tick so state is committed
      openWithSeedRef.current = seedPrompt;
    } else {
      setInput(seedPrompt);
    }
  }, []);

  // Auto-send the seed once panel is open and ready
  useEffect(() => {
    if (open && openWithSeedRef.current) {
      const seed = openWithSeedRef.current;
      openWithSeedRef.current = null;
      setTimeout(() => send(seed), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Cmd+J / Ctrl+J shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggle]);

  // Load suggested prompts when opened first time
  useEffect(() => {
    if (open && user && prompts.length === 0) {
      fetch(`${API}/copilot/suggested-prompts`, { credentials: "include" })
        .then(r => r.ok ? r.json() : { prompts: [] })
        .then(d => setPrompts(d.prompts || []))
        .catch(() => {});
    }
  }, [open, user, prompts.length]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  // Focus input when opened
  useEffect(() => {
    if (open && view === "chat") setTimeout(() => inputRef.current?.focus(), 200);
  }, [open, view]);

  const fetchSessions = async () => {
    try {
      const r = await fetch(`${API}/copilot/sessions`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setSessions(d.sessions || []);
      }
    } catch {}
  };

  const loadSession = async (sid) => {
    setLoading(true);
    setView("chat");
    try {
      const r = await fetch(`${API}/copilot/session/${sid}`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setSessionId(sid);
        setMessages(d.messages || []);
      }
    } catch {} finally { setLoading(false); }
  };

  const newChat = () => {
    setSessionId(null);
    setMessages([]);
    setInput("");
    setView("chat");
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await fetch(`${API}/copilot/session/${sid}`, { method: "DELETE", credentials: "include" });
      setSessions(prev => prev.filter(s => s.session_id !== sid));
      if (sessionId === sid) newChat();
    } catch {}
  };

  // 429 rate-limit awareness
  const [rateLimited, setRateLimited] = useState(null); // { message, retryAfter }

  const send = async (msgText) => {
    const text = (msgText ?? input).trim();
    if (!text || loading) return;
    setInput("");
    setRateLimited(null);
    const userMsg = { role: "user", content: text, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);
    try {
      const r = await fetch(`${API}/copilot/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (r.status === 429) {
        const data = await r.json().catch(() => ({}));
        const retryAfter = parseInt(r.headers.get("Retry-After") || "0", 10);
        const detail = data.detail || "Rate limit reached. Please wait.";
        setRateLimited({ message: detail, retryAfter });
        setMessages(prev => [...prev, { role: "assistant", content: `⏱️ ${detail}`, created_at: new Date().toISOString() }]);
      } else if (r.ok) {
        const d = await r.json();
        setSessionId(d.session_id);
        setMessages(prev => [...prev, { role: "assistant", content: d.reply, created_at: new Date().toISOString() }]);
      } else {
        setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I couldn't process that. Please try again.", created_at: new Date().toISOString() }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Connection error. Please try again.", created_at: new Date().toISOString() }]);
    } finally {
      setLoading(false);
    }
  };

  const ctxValue = useMemo(() => ({ open: openFn, close, toggle, openWithSeed }), [openFn, close, toggle, openWithSeed]);

  return (
    <CoPilotContext.Provider value={ctxValue}>
      {children}
      {user && (
        <AnimatePresence>
          {open && (
            <>
              {/* Mobile backdrop */}
              <motion.div
                className="fixed inset-0 bg-black/60 z-[55] lg:hidden"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={close}
              />
              {/* Slide-in panel */}
              <motion.div
                className="fixed top-0 right-0 bottom-0 w-full lg:w-[420px] bg-obsidian border-l border-[#222] z-[60] flex flex-col shadow-2xl"
                initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
                transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
                data-testid="copilot-panel"
              >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-[#222] bg-gradient-to-r from-crimson/10 to-transparent">
                  <div className="flex items-center gap-2">
                    {view === "history" && (
                      <button onClick={() => setView("chat")} className="text-gray-400 hover:text-white" data-testid="copilot-back-to-chat">
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                    )}
                    <div className="w-7 h-7 bg-crimson flex items-center justify-center rounded-full">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div className="leading-tight">
                      <div className="text-sm font-bold text-white">Rekrutor Co-Pilot</div>
                      <div className="text-[10px] uppercase tracking-wider text-crimson">{view === "history" ? "Conversations" : "AI Assistant"}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => { fetchSessions(); setView(v => v === "history" ? "chat" : "history"); }}
                      className="p-1.5 text-gray-400 hover:text-white hover:bg-white/5 rounded"
                      title="Past conversations"
                      data-testid="copilot-history-btn"
                    >
                      <MessageSquare className="w-4 h-4" />
                    </button>
                    <button onClick={newChat} className="p-1.5 text-gray-400 hover:text-white hover:bg-white/5 rounded" title="New chat" data-testid="copilot-new-btn">
                      <Plus className="w-4 h-4" />
                    </button>
                    <button onClick={close} className="p-1.5 text-gray-400 hover:text-white hover:bg-white/5 rounded" data-testid="copilot-close">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {view === "history" ? (
                  /* Sessions List */
                  <div className="flex-1 overflow-y-auto p-3">
                    {sessions.length === 0 ? (
                      <div className="text-center text-gray-500 text-sm pt-12">No past conversations.</div>
                    ) : sessions.map(s => (
                      <div
                        key={s.session_id}
                        role="button"
                        tabIndex={0}
                        onClick={() => loadSession(s.session_id)}
                        onKeyDown={(e) => { if (e.key === "Enter") loadSession(s.session_id); }}
                        className="w-full text-left px-3 py-2.5 mb-1 bg-[#111] border border-[#222] hover:border-crimson/30 hover:bg-crimson/5 transition-colors group flex items-center gap-2 cursor-pointer"
                        data-testid="copilot-session-item"
                      >
                        <MessageSquare className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-white truncate">{s.title}</div>
                          <div className="text-[10px] text-gray-500">{new Date(s.last_message_at).toLocaleDateString()} · {s.message_count} messages</div>
                        </div>
                        <button onClick={(e) => deleteSession(s.session_id, e)} className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 transition-opacity" aria-label="Delete conversation">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    {/* Messages */}
                    <div ref={scrollRef} className="flex-1 overflow-y-auto p-4" data-testid="copilot-messages">
                      {messages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center px-4">
                          <div className="w-14 h-14 bg-crimson/10 border border-crimson/30 rounded-full flex items-center justify-center mb-3">
                            <Sparkles className="w-6 h-6 text-crimson" />
                          </div>
                          <h3 className="text-base font-bold text-white mb-1">How can I help?</h3>
                          <p className="text-xs text-gray-400 mb-5 max-w-[280px]">
                            Ask me anything about your work on Rekrutor. I have access to your live data.
                          </p>
                          <div className="space-y-2 w-full max-w-[320px]">
                            {prompts.map((p, idx) => (
                              <button
                                key={idx}
                                onClick={() => send(p)}
                                className="w-full text-left px-3 py-2 bg-[#111] border border-[#222] hover:border-crimson/40 hover:bg-crimson/5 text-xs text-gray-300 hover:text-white transition-colors"
                                data-testid={`copilot-prompt-${idx}`}
                              >
                                {p}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <>
                          {messages.map((m, idx) => <MessageBubble key={idx} msg={m} />)}
                          {loading && (
                            <div className="flex justify-start mb-4" data-testid="copilot-loading">
                              <div className="bg-[#1a1a1a] border border-[#2a2a2a] px-3.5 py-3 rounded-2xl rounded-bl-sm">
                                <div className="flex gap-1.5">
                                  <span className="w-1.5 h-1.5 bg-crimson rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                                  <span className="w-1.5 h-1.5 bg-crimson rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                                  <span className="w-1.5 h-1.5 bg-crimson rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Input */}
                    <form
                      onSubmit={(e) => { e.preventDefault(); send(); }}
                      className="p-3 border-t border-[#222] bg-[#0a0a0a]"
                    >
                      <div className="flex items-end gap-2 bg-[#111] border border-[#2a2a2a] focus-within:border-crimson/50 transition-colors p-2">
                        <textarea
                          ref={inputRef}
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              send();
                            }
                          }}
                          placeholder="Ask anything…  (Enter to send, Shift+Enter for newline)"
                          rows={1}
                          className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 outline-none resize-none max-h-32"
                          disabled={loading}
                          data-testid="copilot-input"
                        />
                        <button
                          type="submit"
                          disabled={!input.trim() || loading}
                          className="p-2 bg-crimson hover:bg-crimson/90 disabled:bg-[#222] disabled:text-gray-600 text-white transition-colors shrink-0"
                          data-testid="copilot-send"
                        >
                          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      )}
    </CoPilotContext.Provider>
  );
};

const CoPilot = () => null;
export default CoPilot;
